import { combineReducers } from 'redux';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { api } from './api';
import { environment } from 'environments/environment';
import { Failure } from 'generated/bffCustomer/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { KahlMessage } from 'models/KahlMessage';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectIsUserInternal } from 'saga/auth/ducks';

/* STATE */
export interface KahlState {
  messages: KahlMessage[];
}

/* ACTION TYPES */
export enum KahlActionTypes {
  GetKahl = '@@Kahl/GET_KAHL',
  CloseKahl = '@@Kahl/CLOSE_KAHL',
  GetKahlOutsideComponent = '@@Kahl/GET_KAHL_OUTSIDE_COMPONENT',
}

/* ACTIONS */
export const getKahlActions = createApiActionCreators(KahlActionTypes.GetKahl);
export const closeKahlAction = createActionCreator(KahlActionTypes.CloseKahl);
export const getKahlOutsideComponentAction = createActionCreator(KahlActionTypes.GetKahlOutsideComponent);

/* REDUCERS */
const initialState: KahlState = {
  messages: [],
};

const messages = createReducer(initialState.messages, {
  [KahlActionTypes.GetKahl]: {
    [RequestActionTypes.SUCCESS]: (state: KahlMessage[], payload: KahlMessage) => [...state, ...[payload]],
    [RequestActionTypes.FAILURE]: () => [],
  },
  [KahlActionTypes.CloseKahl]: (state: KahlMessage[], payload: string) =>
    state.filter((message) => message.id !== payload),
});

export default combineReducers<KahlState>({
  messages,
});

/* SELECTORS */
const selectKahl = (state: AppState) => state.kahl;

export const selectKahlMessages = (state: AppState) => selectKahl(state).messages;

/* SAGAS */

function* getInfoMessages({ payload }: any) {
  const kahlMessages: KahlMessage[] = yield select(selectKahlMessages);
  const isInternalUser: boolean = yield select(selectIsUserInternal);

  if (!canProcessKahlMessage(isInternalUser, kahlMessages, payload)) {
    return;
  }

  const failures: Array<Failure> = payload?.failures;
  const messageIds: string | undefined = failures?.find(
    (failure) => failure?.contextData && failure?.contextData['messageIds']
  )?.contextData?.messageIds;
  const stackTraces = failures?.filter((failure) => failure?.stackTrace).map((testStack) => testStack.stackTrace);

  let infoMessageIds: string = payload?.infoMessageIds || messageIds || payload?.kahl || payload?.kahlId;

  if (!infoMessageIds) {
    infoMessageIds = payload?.suppressError ? 'ID0901' : 'ID0013';
  }

  const stackTrace: string | Array<string> = payload?.stackTrace || stackTraces;

  const request: ApiClientRequest = {
    apiCall: call(
      api.getInfoMessages,
      yield getApiUrl(environment.urlConfig.kahl),
      infoMessageIds,
      payload?.exceptionParamMaps
    ),
    skipUserActivityLog: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getKahlActions.success({
        ...resp.data[0],
        id: uuid(),
        kahlId: infoMessageIds,
        stacktrace: stackTrace,
      })
    );
  } else {
    yield put(
      getKahlActions.failure({
        id: uuid(),
        kahlId: infoMessageIds,
        stacktrace: stackTrace,
      })
    );
  }
}

/* EXPORT */
export function* kahlSaga() {
  yield takeEvery(createActionType(KahlActionTypes.GetKahl, RequestActionTypes.REQUEST), getInfoMessages);
  yield takeEvery(createActionType(KahlActionTypes.GetKahlOutsideComponent), getInfoMessages);
}

/**
 * Checks if {@code currentMessage} can be processed (retrieve detail from API + display in application)
 * @param isInternalUser {@code true} if internal user is logged in (all messages will be processed in this case)
 * @param storedMessages already stored KAHL messages
 * @param currentMessage currently processed KAHL message
 */
export const canProcessKahlMessage = (
  isInternalUser: boolean,
  storedMessages: KahlMessage[],
  currentMessage: KahlMessage
) => {
  return isInternalUser || !storedMessages?.some((msg: KahlMessage) => msg.kahlId === currentMessage.kahlId);
};
